import { createSlice } from '@reduxjs/toolkit'
import { isEmpty } from 'lodash-es'
import { parseQueryParams } from 'utils/query'
import { FETCH_STATUSES } from '../../constants/flow'

const { PENDING, SUCCESS, FAILED } = FETCH_STATUSES

/**
 * UI related states.
 */
const initialState = {
  /* modal title will be displayed between Modal back and close button */
  modalTitleContent: null,
  /* modal specific fpti tags */
  modalFptiTags: null,
  /* To show main page spinner */
  showMainPageSpinner: false,
  /* To show page level spinner */
  showModalPageSpinner: false,
  /* To reset page content before redirect to another app or screen in webview */
  resetPageContent: false,
  /* To keep XHR fetch state, i.e. isFetching, status, fetchRetried, lastUpdated */
  fetches: {} as Record<string, any>,
  /* To keep track of latest Fpti Data from XHR requests, last XHR correlation Id
   * will be used for the next page impression to ease the debugging process */
  fptiData: {},
  /* To hide/show unexpected client error, object definition: { fetchName, error } */
  modalUnexpectedClientError: null,
  /* To hide/show unexpected client error for the main page template */
  mainPageUnexpectedClientError: null,
  /* To reference retry function reference from state.fetches */
  modalRetryFetchName: null,
  /* To reference retry function reference for main page template */
  mainPageRetryFetchName: null,
}

const updateFptiData = (state, sysData) => {
  const fptiDataString = sysData?.tracking?.fpti?.dataString
  if (fptiDataString) {
    state.fptiData = parseQueryParams(fptiDataString)
  }
}

const getSpinnerPropertyName = (isFromMainPage) => {
  return isFromMainPage ? 'showMainPageSpinner' : 'showModalPageSpinner'
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    startFetch(state, action) {
      const { fetchName, flowName, isBlocking = true, isFromMainPage = false } = action.payload
      const fetchState = state.fetches[fetchName] || {}
      const spinnerPropName = getSpinnerPropertyName(isFromMainPage)

      state[spinnerPropName] = isBlocking

      state.fetches[fetchName] = {
        flowName,
        isFetching: true,
        status: PENDING,
        fetchRetried: fetchState.fetchRetried,
        lastUpdated: new Date().toISOString(),
        isFromMainPage,
      }
    },
    successfulFetch(state, action) {
      const { fetchName, sysData } = action.payload
      const { flowName, isFromMainPage } = state.fetches[fetchName] || {}
      const spinnerPropName = getSpinnerPropertyName(isFromMainPage)

      state[spinnerPropName] = false

      state.fetches[fetchName] = {
        flowName,
        isFetching: false,
        status: SUCCESS,
        sysData,
        lastUpdated: new Date().toISOString(),
        isFromMainPage,
      }

      if (state.mainPageRetryFetchName === fetchName) {
        state.mainPageRetryFetchName = null
      } else if (state.modalRetryFetchName === fetchName) {
        state.modalRetryFetchName = null
      }

      updateFptiData(state, sysData)
    },
    failedFetch(state, action) {
      const { fetchName, error, sysData } = action.payload
      const fetchState = state.fetches[fetchName] || {}
      const { flowName, isFromMainPage } = state.fetches[fetchName] || {}
      const spinnerPropName = getSpinnerPropertyName(isFromMainPage)

      state[spinnerPropName] = false

      state.fetches[fetchName] = {
        flowName,
        isFetching: false,
        status: FAILED,
        error,
        fetchRetried: fetchState.fetchRetried,
        lastUpdated: new Date().toISOString(),
        isFromMainPage,
      }

      updateFptiData(state, sysData)
    },
    showModalPageSpinner(state) {
      state.showModalPageSpinner = true
    },
    hideModalPageSpinner(state) {
      state.showModalPageSpinner = false
    },
    showMainPageSpinner(state) {
      state.showMainPageSpinner = true
    },
    hideMainPageSpinner(state) {
      state.showMainPageSpinner = false
    },
    updateFetchRetry(state, action) {
      const { fetchName, fetchRetried, retryHandler } = action.payload
      state.fetches[fetchName] = Object.assign({}, state.fetches[fetchName], {
        fetchRetried,
        retryHandler,
      })

      const isFromMainPage = state.fetches[fetchName]?.isFromMainPage
      if (isFromMainPage) {
        state.mainPageRetryFetchName = fetchName
      } else {
        state.modalRetryFetchName = fetchName
      }
    },
    resetFetchRetry(state, action: any) {
      const { isFromMainPage } = action
      if (isFromMainPage) {
        state.mainPageRetryFetchName = null
      } else {
        state.modalRetryFetchName = null
      }
    },
    resetFetch(state, action) {
      const fetchName = action.payload
      if (fetchName) {
        state.fetches[fetchName] = null
      }
    },
    setUnexpectedClientError(state, action) {
      const payload = action.payload
      const { fetchName } = payload
      const isFromMainPage = state.fetches[fetchName]?.isFromMainPage

      if (isFromMainPage) {
        state.mainPageUnexpectedClientError = payload
      } else {
        state.modalUnexpectedClientError = payload
      }
    },
    resetUnexpectedClientError(state, action) {
      const { isFromMainPage } = action.payload
      if (isFromMainPage) {
        state.mainPageUnexpectedClientError = null
      } else {
        state.modalUnexpectedClientError = null
      }
    },
    setModalMetadata(state, action) {
      const { titleContent, fptiTags } = action.payload

      if (titleContent?.id) {
        state.modalTitleContent = titleContent
      }

      if (!isEmpty(fptiTags)) {
        state.modalFptiTags = {
          ...state.modalFptiTags,
          ...fptiTags,
        }
      }
    },
    resetModalMetadata(state) {
      state.modalTitleContent = null
      state.modalFptiTags = null
    },
  },
})

// Flow Modal Selectors
export const selectModalTitleContent = (state) => state.ui.modalTitleContent
export const selectShowModalPageSpinner = (state) => state.ui.showModalPageSpinner
export const selectModalRetryFetchName = (state) => state.ui.modalRetryFetchName
export const selectModalUnexpectedClientError = (state) => state.ui.modalUnexpectedClientError

// Main Page Selectors
export const selectShowMainPageSpinner = (state) => state.ui.showMainPageSpinner
export const selectMainPageRetryFetchName = (state) => state.ui.mainPageRetryFetchName
export const selectMainPageUnexpectedClientError = (state) => state.ui.mainPageUnexpectedClientError

// Common
export const selectFptiData = (state) => state.ui.fptiData
export const selectFetchStatus = (fetchName) => (state) =>
  state.ui.fetches[fetchName]?.status ?? PENDING

export const {
  startFetch,
  successfulFetch,
  failedFetch,
  showModalPageSpinner,
  hideModalPageSpinner,
  showMainPageSpinner,
  hideMainPageSpinner,
  updateFetchRetry,
  resetFetchRetry,
  resetFetch,
  setUnexpectedClientError,
  resetUnexpectedClientError,
  setModalMetadata,
  resetModalMetadata,
} = uiSlice.actions

export default uiSlice.reducer
